import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Activity } from "lucide-react";
import { getScore } from "../lib/api";

/**
 * Compact Pulse Score chip — small circular gauge + numeric score.
 * Designed to sit in the hero greeting row (HomeFeed) or any toolbar.
 *
 * Auto-fetches /api/score on mount. Color is severity-driven:
 *   85+   green       (healthy)
 *   65-84 cyan/primary (steady)
 *   45-64 amber       (watch)
 *   <45   red         (critical)
 */
export default function PulseScoreBadge({ onClick, className = "", showLabel = true, variant = "compact" }) {
  const [score, setScore] = useState(null);
  const [status, setStatus] = useState("");

  useEffect(() => {
    getScore()
      .then(({ data }) => {
        setScore(data?.score ?? null);
        setStatus(data?.status_text || data?.status || "");
      })
      .catch(() => {});
  }, []);

  const tone = toneFor(score);

  if (variant === "hero") {
    return <HeroPulseScore score={score} status={status} tone={tone} onClick={onClick} className={className} />;
  }
  if (variant === "sidebar") {
    return <SidebarPulseScore score={score} status={status} tone={tone} onClick={onClick} className={className} />;
  }

  const circumference = 2 * Math.PI * 14; // r=14
  const offset = score == null ? circumference : circumference - (score / 100) * circumference;

  return (
    <button
      type="button"
      onClick={onClick}
      className={`group relative inline-flex items-center gap-2.5 px-3 py-1.5 rounded-md border border-border bg-card hover:border-primary/40 transition-all ${className}`}
      data-testid="pulse-score-badge"
      title="Pulse Score — בריאות העסק שלך"
    >
      {/* Mini ring */}
      <div className="relative w-8 h-8 shrink-0">
        <svg className="w-8 h-8 -rotate-90" viewBox="0 0 32 32">
          <circle cx="16" cy="16" r="14" fill="none" stroke="#27272A" strokeWidth="2.5" />
          {score != null && (
            <motion.circle
              cx="16"
              cy="16"
              r="14"
              fill="none"
              stroke={tone.hex}
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeDasharray={circumference}
              initial={{ strokeDashoffset: circumference }}
              animate={{ strokeDashoffset: offset }}
              transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1], delay: 0.15 }}
            />
          )}
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          {score == null ? (
            <Activity className="w-3 h-3 text-zinc-600 animate-pulse" />
          ) : (
            <span className={`text-[10px] font-mono font-bold ${tone.text}`}>{score}</span>
          )}
        </div>
      </div>

      {/* Label */}
      {showLabel && (
        <div className="flex flex-col items-start leading-tight text-right">
          <span className="text-[9px] uppercase tracking-widest text-zinc-500 font-mono">
            Pulse Score
          </span>
          <span className={`text-[11px] ${tone.text} font-medium`}>
            {score == null ? "טוען..." : status || tone.label}
          </span>
        </div>
      )}
    </button>
  );
}

function toneFor(s) {
  if (s == null) return { hex: "#27272A", text: "text-zinc-500", label: "—" };
  if (s >= 85) return { hex: "#22C55E", text: "text-emerald-400", label: "מצוין" };
  if (s >= 65) return { hex: "#00E5FF", text: "text-primary", label: "יציב" };
  if (s >= 45) return { hex: "#F59E0B", text: "text-severity-watch", label: "לבדיקה" };
  return { hex: "#EF4444", text: "text-severity-critical", label: "קריטי" };
}

function HeroPulseScore({ score, status, tone, onClick, className = "" }) {
  // Larger ring for a hero / vital-signs feel
  const r = 28;
  const stroke = 4;
  const size = 72;
  const circumference = 2 * Math.PI * r;
  const offset = score == null ? circumference : circumference - (score / 100) * circumference;

  return (
    <button
      type="button"
      onClick={onClick}
      className={`group relative inline-flex items-center gap-4 px-5 py-3 rounded-lg border border-border bg-card/80 backdrop-blur hover:border-primary/40 transition-all overflow-hidden text-right ${className}`}
      data-testid="pulse-score-badge-hero"
      title="Pulse Score — בריאות העסק שלך"
    >
      {/* Soft tone glow */}
      <div
        className="absolute -top-10 -left-10 w-28 h-28 rounded-full blur-3xl opacity-30 pointer-events-none transition-opacity"
        style={{ background: tone.hex }}
      />

      <div className="relative shrink-0" style={{ width: size, height: size }}>
        <svg className="-rotate-90" width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
          <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#27272A" strokeWidth={stroke} />
          {score != null && (
            <motion.circle
              cx={size / 2}
              cy={size / 2}
              r={r}
              fill="none"
              stroke={tone.hex}
              strokeWidth={stroke}
              strokeLinecap="round"
              strokeDasharray={circumference}
              initial={{ strokeDashoffset: circumference }}
              animate={{ strokeDashoffset: offset }}
              transition={{ duration: 1.4, ease: [0.16, 1, 0.3, 1], delay: 0.15 }}
            />
          )}
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          {score == null ? (
            <Activity className="w-5 h-5 text-zinc-600 animate-pulse" />
          ) : (
            <>
              <span className={`text-2xl font-mono font-bold ${tone.text} leading-none`}>{score}</span>
              <span className="text-[8px] uppercase tracking-widest text-zinc-500 font-mono mt-0.5">/100</span>
            </>
          )}
        </div>
      </div>

      <div className="flex flex-col leading-tight">
        <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 font-mono">
          Pulse Score · בריאות העסק
        </div>
        <div className={`text-base font-medium ${tone.text} mt-0.5`}>
          {score == null ? "טוען נתונים..." : (status || tone.label)}
        </div>
        <div className="text-[10px] text-zinc-500 mt-1 font-mono">
          לחץ כדי לראות פירוט מלא
        </div>
      </div>
    </button>
  );
}

function SidebarPulseScore({ score, status, tone, onClick, className = "" }) {
  // Sidebar variant — horizontal card matching nav-item proportions.
  // Acts as a "vital sign" badge visible from every page.
  const r = 16;
  const stroke = 2.5;
  const size = 40;
  const circumference = 2 * Math.PI * r;
  const offset = score == null ? circumference : circumference - (score / 100) * circumference;

  return (
    <button
      type="button"
      onClick={onClick}
      className={`group relative flex items-center gap-2.5 px-2.5 py-2 rounded-md bg-white/[0.02] border border-white/[0.05] hover:bg-white/[0.04] hover:border-primary/30 transition-all overflow-hidden text-right ${className}`}
      data-testid="pulse-score-badge-sidebar"
      title="Pulse Score — בריאות העסק שלך"
    >
      {/* Soft tone glow */}
      <div
        className="absolute -top-6 -left-6 w-20 h-20 rounded-full blur-2xl opacity-20 pointer-events-none transition-opacity group-hover:opacity-30"
        style={{ background: tone.hex }}
      />

      <div className="relative shrink-0" style={{ width: size, height: size }}>
        <svg className="-rotate-90" width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
          <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#27272A" strokeWidth={stroke} />
          {score != null && (
            <motion.circle
              cx={size / 2}
              cy={size / 2}
              r={r}
              fill="none"
              stroke={tone.hex}
              strokeWidth={stroke}
              strokeLinecap="round"
              strokeDasharray={circumference}
              initial={{ strokeDashoffset: circumference }}
              animate={{ strokeDashoffset: offset }}
              transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1], delay: 0.15 }}
            />
          )}
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          {score == null ? (
            <Activity className="w-3.5 h-3.5 text-zinc-600 animate-pulse" />
          ) : (
            <span className={`text-xs font-mono font-bold ${tone.text} leading-none`}>{score}</span>
          )}
        </div>
      </div>

      <div className="flex-1 min-w-0 leading-tight relative">
        <div className="text-[9px] uppercase tracking-widest text-zinc-500 font-mono">
          Pulse Score
        </div>
        <div className={`text-[11px] ${tone.text} font-medium truncate`}>
          {score == null ? "טוען..." : (status || tone.label)}
        </div>
      </div>
    </button>
  );
}
